import { useEffect, useRef, useState } from 'react';
import type { FormEvent, JSX } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  EVENT_FORMATS,
  EVENT_FORMAT_LABELS,
  EVENT_LEVELS,
  EVENT_LEVEL_LABELS,
  EVENT_STATUSES,
  EVENT_STATUS_LABELS,
  exceptionAssetsInTote,
  isExceptionStatus,
  isPackableAsset,
  roleCanEditEvents,
  roleCanInventory,
  roleCanManageEventCodes,
  roleCanSendZoho,
  shippedViaOptionsFrom,
  type EventAccessCodeCreateResponse,
  type EventAccessCodeListResponse,
  type PublicEventAccessCode,
  type AssignmentConflict,
  type AssignmentRequestDecisionResponse,
  type AssignmentRequestListResponse,
  type ConflictCheckResponse,
  type EventOptionsResponse,
  type EventResponse,
  type EventStatus,
  type OpenAssignmentRequest,
  type PublicAsset,
  type PublicEvent,
  type PublicLoadout,
  type ShipmentResponse,
} from '@inventohub/shared';
import { CheckboxDropdown } from '../components/CheckboxDropdown';
import { SearchablePickList } from '../components/SearchablePickList';
import { EventSummaryLines } from '../components/EventSummaryLines';
import { HelpLink } from '../components/HelpLink';
import { ApiClientError, apiRequest } from '../lib/api';
import { useAuth } from '../lib/auth';

interface Draft {
  name: string;
  startDate: string;
  endDate: string;
  confirmed: boolean;
  status: EventStatus;
  organizationId: string;
  organizationName: string;
  locationText: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  contractText: string;
  matsRequested: string;
  bracketPlatform: string;
  streamingPlatform: string;
  format: string;
  levels: string[];
  equipmentNeedsText: string;
  priority: string;
  notes: string;
  staffUserIds: string[];
  trailerIds: string[];
  shipmentIds: string[];
  newShipmentName: string;
  linkEventId: string;
}

const EMPTY: Draft = {
  name: '',
  startDate: '',
  endDate: '',
  confirmed: false,
  status: 'draft',
  organizationId: '',
  organizationName: '',
  locationText: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  contractText: '',
  matsRequested: '',
  bracketPlatform: 'floarena',
  streamingPlatform: 'none',
  format: '',
  levels: [],
  equipmentNeedsText: '',
  priority: '',
  notes: '',
  staffUserIds: [],
  trailerIds: [],
  shipmentIds: [],
  newShipmentName: '',
  linkEventId: '',
};

function fromEvent(event: PublicEvent, loadout: PublicLoadout): Draft {
  return {
    name: event.name,
    startDate: event.startDate,
    endDate: event.endDate,
    confirmed: event.confirmed,
    status: event.status,
    organizationId: event.organizationId ?? '',
    organizationName: '',
    locationText: event.locationText ?? '',
    contactName: event.contactName ?? '',
    contactEmail: event.contactEmail ?? '',
    contactPhone: event.contactPhone ?? '',
    contractText: event.contractText ?? '',
    matsRequested: event.matsRequested === null ? '' : String(event.matsRequested),
    bracketPlatform: event.bracketPlatform,
    streamingPlatform: event.streamingPlatform,
    format: event.format ?? '',
    levels: event.levels ?? [],
    equipmentNeedsText: event.equipmentNeedsText ?? '',
    priority: event.priority === null ? '' : String(event.priority),
    notes: event.notes ?? '',
    staffUserIds: loadout.staff.map((row) => row.userId),
    trailerIds: loadout.trailers.map((trailer) => trailer.id),
    shipmentIds: (loadout.shipments ?? []).map((shipment) => shipment.id),
    newShipmentName: '',
    linkEventId: '',
  };
}

function toggleId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((item) => item !== id) : [...ids, id];
}

function assignmentBody(draft: Draft): Record<string, unknown> {
  return {
    staffUserIds: draft.staffUserIds,
    trailerIds: draft.trailerIds,
    shipmentIds: draft.shipmentIds,
  };
}

function eventBody(draft: Draft): Record<string, unknown> {
  return {
    name: draft.name,
    startDate: draft.startDate,
    endDate: draft.endDate,
    confirmed: draft.confirmed,
    status: draft.status,
    organizationId: draft.organizationId || null,
    organizationName: draft.organizationName || undefined,
    locationText: draft.locationText || null,
    contactName: draft.contactName || null,
    contactEmail: draft.contactEmail || null,
    contactPhone: draft.contactPhone || null,
    contractText: draft.contractText || null,
    matsRequested: draft.matsRequested === '' ? null : Number(draft.matsRequested),
    bracketPlatform: draft.bracketPlatform,
    streamingPlatform: draft.streamingPlatform,
    format: draft.format || null,
    levels: draft.levels,
    equipmentNeedsText: draft.equipmentNeedsText || null,
    priority: draft.priority === '' ? null : Number(draft.priority),
    notes: draft.notes || null,
    ...assignmentBody(draft),
  };
}

export function EventDetailPage(): JSX.Element {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, code } = useAuth();
  const canEdit = user ? roleCanEditEvents(user.role) : false;
  const canManageCodes = user ? roleCanManageEventCodes(user.role) : false;
  const canSendZoho = user ? roleCanSendZoho(user.role) : false;
  const isNew = id === 'new';
  const [options, setOptions] = useState<EventOptionsResponse | null>(null);
  const [event, setEvent] = useState<PublicEvent | null>(null);
  const [loadout, setLoadout] = useState<PublicLoadout | null>(null);
  const [draft, setDraftState] = useState<Draft>(EMPTY);
  const draftRef = useRef(draft);
  const [conflicts, setConflicts] = useState<AssignmentConflict[]>([]);
  const [pendingSave, setPendingSave] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [openRequests, setOpenRequests] = useState<{
    eventId: string;
    requests: OpenAssignmentRequest[];
  } | null>(null);
  const [requestTick, setRequestTick] = useState(0);
  const [requestBusy, setRequestBusy] = useState<string | null>(null);

  function setDraft(update: Draft | ((current: Draft) => Draft)): void {
    const next = typeof update === 'function' ? update(draftRef.current) : update;
    draftRef.current = next;
    setDraftState(next);
  }

  useEffect(() => {
    if (code) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<EventOptionsResponse>('/api/events/options');
        if (!cancelled) setOptions(body);
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(caught instanceof ApiClientError ? caught.message : 'Could not load options.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [code]);

  useEffect(() => {
    if (isNew || !id) return;
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<EventResponse>(`/api/events/${id}`);
        if (!cancelled) {
          setEvent(body.event);
          setLoadout(body.loadout);
          setDraft(fromEvent(body.event, body.loadout));
          setConflicts(body.loadout.conflicts);
          setError(null);
        }
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(caught instanceof ApiClientError ? caught.message : 'Could not load the event.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isNew]);

  useEffect(() => {
    if (!canEdit || isNew || !id) return;
    let cancelled = false;
    const eventId = id;
    void (async () => {
      try {
        const body = await apiRequest<AssignmentRequestListResponse>(
          `/api/events/${eventId}/assignment-requests`,
        );
        if (!cancelled) setOpenRequests({ eventId, requests: body.requests });
      } catch {
        if (!cancelled) setOpenRequests({ eventId, requests: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canEdit, isNew, id, requestTick]);

  const shownRequests =
    canEdit && !isNew && id && openRequests?.eventId === id ? openRequests.requests : [];
  const loading = !isNew && !loadout && !error;
  const showStack = Boolean(loadout) || canEdit;

  async function save(force: boolean): Promise<void> {
    setError(null);
    setNotice(null);
    const current = draftRef.current;
    const payload = eventBody(current);
    try {
      if (!force) {
        const previewPath = loadout
          ? `/api/loadouts/${loadout.id}/conflict-check`
          : '/api/events/conflict-check';
        const preview = await apiRequest<ConflictCheckResponse>(previewPath, {
          method: 'POST',
          body: loadout ? assignmentBody(current) : payload,
        });
        if (preview.conflicts.length > 0) {
          setConflicts(preview.conflicts);
          setPendingSave(true);
          return;
        }
      }
      const body = isNew
        ? await apiRequest<EventResponse>('/api/events', { method: 'POST', body: payload })
        : await apiRequest<EventResponse>(`/api/events/${id}`, { method: 'PATCH', body: payload });
      setEvent(body.event);
      setLoadout(body.loadout);
      setDraft(fromEvent(body.event, body.loadout));
      setConflicts(body.loadout.conflicts);
      setPendingSave(false);
      if (body.loadout.conflicts.length > 0) {
        setNotice('Saved with overlapping-assignment warnings. Priority does not reallocate gear.');
      } else {
        setNotice('Saved.');
      }
      if (isNew) navigate(`/events/${body.event.id}`, { replace: true });
      setRequestTick((tick) => tick + 1);
    } catch (caught: unknown) {
      setPendingSave(false);
      setError(caught instanceof ApiClientError ? caught.message : 'Could not save the event.');
    }
  }

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    await save(false);
  }

  async function createInlineShipment(): Promise<void> {
    const name = draftRef.current.newShipmentName.trim();
    if (!name) return;
    setError(null);
    try {
      const body = await apiRequest<ShipmentResponse>('/api/shipments', {
        method: 'POST',
        body: { name },
      });
      setOptions((current) =>
        current
          ? {
              ...current,
              shipments: [...(current.shipments ?? []), body.shipment].sort((a, b) =>
                a.name.localeCompare(b.name),
              ),
            }
          : current,
      );
      setDraft((current) => ({
        ...current,
        shipmentIds: current.shipmentIds.includes(body.shipment.id)
          ? current.shipmentIds
          : [...current.shipmentIds, body.shipment.id],
        newShipmentName: '',
      }));
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiClientError ? caught.message : 'Could not create the shipment.',
      );
    }
  }

  async function onAcceptRequest(userId: string): Promise<void> {
    if (!id || isNew) return;
    setRequestBusy(userId);
    setError(null);
    try {
      const body = await apiRequest<AssignmentRequestDecisionResponse>(
        `/api/events/${id}/assignment-requests/${userId}/accept`,
        { method: 'POST', body: {} },
      );
      // Only the accepted person joins the draft. Other unsaved edits stay put.
      setDraft((current) => ({
        ...current,
        staffUserIds: current.staffUserIds.includes(userId)
          ? current.staffUserIds
          : [...current.staffUserIds, userId],
      }));
      if (body.loadout) {
        setLoadout(body.loadout);
        setConflicts(body.loadout.conflicts);
      }
      setOpenRequests((current) =>
        current && current.eventId === id
          ? { ...current, requests: current.requests.filter((row) => row.userId !== userId) }
          : current,
      );
      setNotice('Accepted. They are on the crew.');
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiClientError ? caught.message : 'Could not accept that request.',
      );
    } finally {
      setRequestBusy(null);
    }
  }

  async function onDeclineRequest(userId: string): Promise<void> {
    if (!id || isNew) return;
    setRequestBusy(userId);
    setError(null);
    try {
      await apiRequest(`/api/events/${id}/assignment-requests/${userId}/decline`, {
        method: 'POST',
        body: {},
      });
      setOpenRequests((current) =>
        current && current.eventId === id
          ? { ...current, requests: current.requests.filter((row) => row.userId !== userId) }
          : current,
      );
      setNotice('Declined. They were not added to the crew.');
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiClientError ? caught.message : 'Could not decline that request.',
      );
    } finally {
      setRequestBusy(null);
    }
  }

  async function onLink(): Promise<void> {
    const linkEventId = draftRef.current.linkEventId;
    if (!id || isNew || !linkEventId) return;
    setError(null);
    try {
      const body = await apiRequest<EventResponse>(`/api/events/${id}/link`, {
        method: 'POST',
        body: { eventId: linkEventId },
      });
      setEvent(body.event);
      setLoadout(body.loadout);
      setDraft(fromEvent(body.event, body.loadout));
      setConflicts(body.loadout.conflicts);
      setNotice('Events now share one loadout and one pack list.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not link the events.');
    }
  }

  if (loading) {
    return (
      <section className="card">
        <p>Loading event…</p>
      </section>
    );
  }

  return (
    <section data-testid="event-detail-page">
      <div className="card">
        <p className="meta">
          <Link to="/events">Events</Link>
        </p>
        <div className="card__title-row">
          <h2>{isNew ? 'Create event' : draft.name || 'Event'}</h2>
          <HelpLink slug="event-edit" />
        </div>
        {!isNew && event ? <EventSummaryLines event={event} testId="event-header-summary" /> : null}
        {error ? <p className="status status--error">{error}</p> : null}
        {notice ? <p className="status status--ok">{notice}</p> : null}
        {conflicts.length > 0 ? (
          <div className="status status--warn" data-testid="conflict-warning">
            <p>Overlapping assignments on unlinked loadouts:</p>
            <ul>
              {conflicts.map((conflict) => (
                <li key={`${conflict.kind}-${conflict.resourceId}-${conflict.otherLoadoutId}`}>
                  {conflict.kind === 'availability'
                    ? `${conflict.resourceLabel} is marked unavailable on ${conflict.otherEventNames.join(', ')}`
                    : `${conflict.kind}: ${conflict.resourceLabel}${
                        conflict.otherEventNames.length > 0
                          ? ` also on ${conflict.otherEventNames.join(', ')}`
                          : ''
                      }`}
                </li>
              ))}
            </ul>
            {pendingSave && canEdit ? (
              <button
                type="button"
                className="button"
                data-testid="save-anyway"
                onClick={() => void save(true)}
              >
                Save anyway
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="event-page-columns" data-testid="event-page-columns">
        <div
          className="event-page-column event-page-column--edit"
          data-testid="event-page-edit-column"
        >
          {canEdit ? (
            <form
              className="card"
              onSubmit={(event) => void onSubmit(event)}
              data-testid="event-form"
            >
              <h2>{isNew ? 'New event' : 'Edit event'}</h2>
              <label className="field">
                Name
                <input
                  value={draft.name}
                  onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                  required
                  data-testid="event-name"
                />
              </label>
              <label className="field">
                Start date
                <input
                  type="date"
                  value={draft.startDate}
                  onChange={(event) => setDraft({ ...draft, startDate: event.target.value })}
                  required
                  data-testid="event-start"
                />
              </label>
              <label className="field">
                End date
                <input
                  type="date"
                  value={draft.endDate}
                  onChange={(event) => setDraft({ ...draft, endDate: event.target.value })}
                  required
                  data-testid="event-end"
                />
              </label>
              <label className="field">
                Status
                <select
                  value={draft.status}
                  onChange={(event) => {
                    const status = event.target.value as EventStatus;
                    setDraft({
                      ...draft,
                      status,
                      confirmed: status === 'locked' ? true : draft.confirmed,
                    });
                  }}
                  data-testid="event-status"
                >
                  {EVENT_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {EVENT_STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={draft.confirmed}
                  onChange={(event) => setDraft({ ...draft, confirmed: event.target.checked })}
                  data-testid="event-confirmed"
                />
                Confirmed
              </label>
              <label className="field">
                Organization
                <select
                  value={draft.organizationId}
                  onChange={(event) =>
                    setDraft({ ...draft, organizationId: event.target.value, organizationName: '' })
                  }
                  data-testid="event-organization"
                >
                  <option value="">None</option>
                  {(options?.organizations ?? []).map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                Add organization
                <input
                  value={draft.organizationName}
                  onChange={(event) => setDraft({ ...draft, organizationName: event.target.value })}
                  placeholder="Type a name to create inline"
                  data-testid="event-organization-new"
                />
              </label>
              <label className="field">
                Location
                <input
                  value={draft.locationText}
                  onChange={(event) => setDraft({ ...draft, locationText: event.target.value })}
                  data-testid="event-location"
                />
              </label>
              <label className="field">
                Tournament director name
                <input
                  value={draft.contactName}
                  onChange={(event) => setDraft({ ...draft, contactName: event.target.value })}
                  data-testid="event-td-name"
                />
              </label>
              <label className="field">
                Tournament director email
                <input
                  type="email"
                  value={draft.contactEmail}
                  onChange={(event) => setDraft({ ...draft, contactEmail: event.target.value })}
                  data-testid="event-td-email"
                />
              </label>
              <label className="field">
                Tournament director phone
                <input
                  value={draft.contactPhone}
                  onChange={(event) => setDraft({ ...draft, contactPhone: event.target.value })}
                  data-testid="event-td-phone"
                />
              </label>
              <label className="field">
                Contract
                <textarea
                  value={draft.contractText}
                  onChange={(event) => setDraft({ ...draft, contractText: event.target.value })}
                />
              </label>
              <label className="field">
                Mats requested
                <input
                  inputMode="numeric"
                  value={draft.matsRequested}
                  onChange={(event) => setDraft({ ...draft, matsRequested: event.target.value })}
                />
              </label>
              <label className="field">
                Bracket platform
                <select
                  value={draft.bracketPlatform}
                  onChange={(event) => setDraft({ ...draft, bracketPlatform: event.target.value })}
                  data-testid="event-bracket"
                >
                  {(options?.bracketPlatforms ?? []).map((option) => (
                    <option key={option.slug} value={option.slug}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                Streaming platform
                <select
                  value={draft.streamingPlatform}
                  onChange={(event) =>
                    setDraft({ ...draft, streamingPlatform: event.target.value })
                  }
                  data-testid="event-streaming"
                >
                  {(options?.streamingPlatforms ?? []).map((option) => (
                    <option key={option.slug} value={option.slug}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                Format
                <select
                  value={draft.format}
                  onChange={(event) => setDraft({ ...draft, format: event.target.value })}
                >
                  <option value="">Unset</option>
                  {EVENT_FORMATS.map((format) => (
                    <option key={format} value={format}>
                      {EVENT_FORMAT_LABELS[format]}
                    </option>
                  ))}
                </select>
              </label>
              <CheckboxDropdown
                label="Level"
                options={EVENT_LEVELS.map((level) => ({
                  value: level,
                  label: EVENT_LEVEL_LABELS[level],
                }))}
                selected={draft.levels}
                onChange={(levels) => setDraft({ ...draft, levels })}
                emptyLabel="Unset"
                testId="event-level"
              />
              <label className="field">
                Equipment needs
                <textarea
                  value={draft.equipmentNeedsText}
                  onChange={(event) =>
                    setDraft({ ...draft, equipmentNeedsText: event.target.value })
                  }
                  placeholder="Third-party trailers belong here, not in the trailer list."
                />
              </label>
              <label className="field">
                Priority (display only)
                <input
                  inputMode="numeric"
                  value={draft.priority}
                  onChange={(event) => setDraft({ ...draft, priority: event.target.value })}
                  data-testid="event-priority"
                />
              </label>
              <label className="field">
                Notes
                <textarea
                  value={draft.notes}
                  onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
                />
              </label>

              <button type="submit" className="button" data-testid="save-event">
                Save event
              </button>
            </form>
          ) : (
            <div className="card" data-testid="event-readonly-note">
              <p className="meta">Tournament directors and viewers cannot create or edit events.</p>
            </div>
          )}
          {canSendZoho && event && !isNew ? (
            <ZohoEstimateCard
              key={event.id}
              event={event}
              onEvent={(next) => {
                setEvent(next);
              }}
            />
          ) : null}
        </div>

        {showStack ? (
          <div
            className="event-page-column event-page-column--stack"
            data-testid="event-page-stack"
          >
            {loadout ? (
              <PackList
                loadout={loadout}
                event={event}
                canInventory={Boolean(code || (user && roleCanInventory(user.role)))}
                assets={options?.assets ?? []}
              />
            ) : null}
            {canEdit ? (
              <div className="card event-assignments" data-testid="event-assignments">
                <h2>Assignments</h2>
                {shownRequests.length > 0 ? (
                  <div data-testid="assignment-requests">
                    <h3>Requests</h3>
                    <ul className="link-list">
                      {shownRequests.map((row) => (
                        <li key={row.userId} data-testid={`assignment-request-${row.userId}`}>
                          <p>{row.name}</p>
                          <div className="button-row">
                            <button
                              type="button"
                              className="button button--compact"
                              disabled={requestBusy === row.userId}
                              onClick={() => void onAcceptRequest(row.userId)}
                              data-testid={`assignment-request-accept-${row.userId}`}
                            >
                              Accept
                            </button>
                            <button
                              type="button"
                              className="button button--ghost button--compact"
                              disabled={requestBusy === row.userId}
                              onClick={() => void onDeclineRequest(row.userId)}
                              data-testid={`assignment-request-decline-${row.userId}`}
                            >
                              Decline
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <fieldset className="fieldset">
                  <legend>Staff</legend>
                  {(options?.users ?? []).map((member) => (
                    <label className="check" key={member.userId}>
                      <input
                        type="checkbox"
                        checked={draft.staffUserIds.includes(member.userId)}
                        onChange={() =>
                          setDraft((current) => ({
                            ...current,
                            staffUserIds: toggleId(current.staffUserIds, member.userId),
                          }))
                        }
                        data-testid={`assign-staff-${member.userId}`}
                      />
                      {member.name}
                    </label>
                  ))}
                </fieldset>
                <SearchablePickList
                  legend="Shipped Via"
                  items={shippedViaOptionsFrom(options?.trailers ?? [], options?.shipments ?? [])}
                  queryPlaceholder="Trailer or shipment name"
                  testId="shipped-via"
                  filter={(option, query) => option.name.toLowerCase().includes(query)}
                  renderItem={(option) => {
                    const selected =
                      option.kind === 'trailer'
                        ? draft.trailerIds.includes(option.id)
                        : draft.shipmentIds.includes(option.id);
                    return (
                      <label className="check">
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={() => {
                            if (option.kind === 'trailer') {
                              setDraft((current) => ({
                                ...current,
                                trailerIds: toggleId(current.trailerIds, option.id),
                              }));
                              return;
                            }
                            setDraft((current) => ({
                              ...current,
                              shipmentIds: toggleId(current.shipmentIds, option.id),
                            }));
                          }}
                          data-testid={
                            option.kind === 'trailer'
                              ? `assign-trailer-${option.name}`
                              : `assign-shipment-${option.name}`
                          }
                        />
                        {option.kind === 'trailer' ? 'Trailer' : 'Shipment'}: {option.name}
                      </label>
                    );
                  }}
                />
                <label className="field">
                  New shipment
                  <input
                    value={draft.newShipmentName}
                    onChange={(event) =>
                      setDraft({ ...draft, newShipmentName: event.target.value })
                    }
                    placeholder="Pickup — Norman duals"
                    data-testid="inline-shipment-name"
                  />
                </label>
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => void createInlineShipment()}
                  data-testid="inline-shipment-create"
                >
                  Create shipment and assign
                </button>
              </div>
            ) : null}
            {canManageCodes && loadout && !isNew ? <EventCodesCard loadoutId={loadout.id} /> : null}
            {canEdit && !isNew && loadout ? (
              <div className="card" data-testid="linked-events">
                <h2>Linked events</h2>
                <p className="meta">
                  Saturday + Sunday can share one loadout, assignments, and pack list.
                </p>
                <ul className="link-list">
                  {loadout.events.map((event) => (
                    <li key={event.id}>
                      <Link to={`/events/${event.id}`}>{event.name}</Link>
                    </li>
                  ))}
                </ul>
                <label className="field">
                  Link another event
                  <input
                    value={draft.linkEventId}
                    onChange={(event) => setDraft({ ...draft, linkEventId: event.target.value })}
                    placeholder="Event UUID"
                    data-testid="link-event-id"
                  />
                </label>
                <button
                  type="button"
                  className="button button--ghost"
                  onClick={() => void onLink()}
                  data-testid="link-event"
                >
                  Link and share loadout
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function PackList({
  loadout,
  event,
  canInventory,
  assets,
}: {
  loadout: PublicLoadout;
  event: PublicEvent | null;
  canInventory: boolean;
  assets: PublicAsset[];
}): JSX.Element {
  const packedMats = loadout.assets.filter(isPackableAsset);
  return (
    <div className="card" data-testid="pack-list">
      <h2>Pack list</h2>
      {event ? <EventSummaryLines event={event} testId="pack-list-summary" /> : null}
      <p className="meta">
        Loadout {loadout.span ? `${loadout.span.start} – ${loadout.span.end}` : 'with no dates yet'}
        . Linked events share this list.
      </p>
      <p>
        <strong>Staff:</strong>{' '}
        {loadout.staff.length > 0 ? loadout.staff.map((row) => row.name).join(', ') : 'None'}
      </p>
      <p data-testid="pack-list-shipped-via">
        <strong>Shipped Via:</strong>{' '}
        {[
          ...loadout.trailers.map((row) => row.name),
          ...(loadout.shipments ?? []).map((row) => row.name),
        ].join(', ') || 'None'}
      </p>
      <p className="meta">Pack contents of that Shipped Via:</p>
      <p>
        <strong>Totes:</strong>{' '}
        {loadout.totes.length > 0 ? loadout.totes.map((tote) => tote.number).join(', ') : 'None'}
      </p>
      {loadout.totes.map((tote) => {
        const exceptions = exceptionAssetsInTote(tote.id, assets);
        if (exceptions.length === 0) return null;
        return (
          <p
            key={tote.id}
            className="status status--warn"
            data-testid={`pack-tote-warning-${tote.number}`}
          >
            Tote {tote.number} contains{' '}
            {exceptions.map((asset) => `${asset.serial} (${asset.status})`).join(', ')}.
          </p>
        );
      })}
      {canInventory && loadout.totes.length > 0 ? (
        <ul className="link-list">
          {loadout.totes.map((tote) => (
            <li key={tote.id}>
              <Link
                to={`/inventory/new?tote=${encodeURIComponent(tote.number)}&loadout=${loadout.id}&stage=venue_receive`}
                data-testid={`inventory-tote-${tote.number}`}
              >
                Inventory tote {tote.number}
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
      <p>
        <strong>Mats:</strong>{' '}
        {packedMats.length > 0 ? packedMats.map((asset) => asset.serial).join(', ') : 'None'}
      </p>
      {packedMats
        .filter((asset) => isExceptionStatus(asset.status))
        .map((asset) => (
          <p
            key={asset.id}
            className="status status--warn"
            data-testid={`pack-asset-warning-${asset.serial}`}
          >
            {asset.serial} is {asset.status}. Current loadout assignment is unchanged.
          </p>
        ))}
    </div>
  );
}

function EventCodesCard({ loadoutId }: { loadoutId: string }): JSX.Element {
  const [codes, setCodes] = useState<PublicEventAccessCode[]>([]);
  const [displayName, setDisplayName] = useState('');
  const [raw, setRaw] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function loadCodes(): Promise<void> {
    const body = await apiRequest<EventAccessCodeListResponse>(`/api/loadouts/${loadoutId}/codes`);
    setCodes(body.codes);
  }

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<EventAccessCodeListResponse>(
          `/api/loadouts/${loadoutId}/codes`,
        );
        if (!cancelled) setCodes(body.codes);
      } catch (caught: unknown) {
        if (!cancelled) {
          setError(caught instanceof ApiClientError ? caught.message : 'Could not load codes.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadoutId]);

  async function onIssue(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setRaw(null);
    setBusy(true);
    try {
      const body = await apiRequest<EventAccessCodeCreateResponse>(
        `/api/loadouts/${loadoutId}/codes`,
        { method: 'POST', body: { displayName } },
      );
      setRaw(body.raw);
      setDisplayName('');
      await loadCodes();
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not issue a code.');
    } finally {
      setBusy(false);
    }
  }

  async function onRevoke(codeId: string): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await apiRequest(`/api/loadouts/${loadoutId}/codes/${codeId}/revoke`, { method: 'POST' });
      await loadCodes();
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not revoke that code.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" data-testid="event-codes">
      <div className="card__title-row">
        <h2>Tournament director codes</h2>
        <HelpLink slug="event-codes" />
      </div>
      <p className="meta">
        Bound to this loadout, including linked events. The raw code is shown once. After that, only
        the display name and last two characters are kept.
      </p>
      {error ? <p className="status status--error">{error}</p> : null}
      {raw ? (
        <p className="status status--ok" data-testid="raw-event-code">
          Copy this code now. It will not be shown again: {raw}
        </p>
      ) : null}
      <form onSubmit={(event) => void onIssue(event)}>
        <label className="field">
          Display name
          <input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            required
            data-testid="event-code-display-name"
          />
        </label>
        <button type="submit" className="button" disabled={busy} data-testid="issue-event-code">
          Issue code
        </button>
      </form>
      <ul className="link-list" data-testid="event-code-list">
        {codes.map((row) => (
          <li key={row.id} data-testid={`event-code-${row.id}`}>
            <p>
              {row.displayName} · ends with {row.hint}
              {row.revokedAt ? ' · revoked' : ''}
            </p>
            <p className="meta">Expires {row.expiresAt}</p>
            {!row.revokedAt ? (
              <button
                type="button"
                className="button button--ghost"
                disabled={busy}
                onClick={() => void onRevoke(row.id)}
                data-testid={`revoke-event-code-${row.id}`}
              >
                Revoke
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function dollarsFromCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '';
  return (cents / 100).toFixed(2);
}

function centsFromDollarInput(raw: string): number | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const dollars = Number(trimmed);
  if (!Number.isFinite(dollars) || dollars <= 0) return null;
  const cents = Math.round(dollars * 100);
  return cents >= 1 ? cents : null;
}

function ZohoEstimateCard({
  event,
  onEvent,
}: {
  event: PublicEvent;
  onEvent: (event: PublicEvent) => void;
}): JSX.Element {
  const [quotedPrice, setQuotedPrice] = useState(dollarsFromCents(event.quotedPriceCents));
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const hasEstimate = Boolean(event.zohoEstimateNumber);
  const hasInvoice = Boolean(event.zohoInvoiceNumber);
  const displayedPrice = hasEstimate ? dollarsFromCents(event.quotedPriceCents) : quotedPrice;
  const cents = centsFromDollarInput(displayedPrice);
  const canSend = Boolean(event.organizationId) && cents !== null && !hasEstimate && !busy;
  const canConvert = hasEstimate && !hasInvoice && !busy;

  async function sendEstimate(): Promise<void> {
    if (cents === null) return;
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const body = await apiRequest<EventResponse>(`/api/events/${event.id}/zoho/estimate`, {
        method: 'POST',
        body: { quoted_price_cents: cents },
      });
      onEvent(body.event);
      setNotice('Draft estimate sent to Zoho Books.');
    } catch (caught: unknown) {
      setError(caught instanceof ApiClientError ? caught.message : 'Could not send the estimate.');
    } finally {
      setBusy(false);
    }
  }

  async function convertInvoice(): Promise<void> {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      const body = await apiRequest<EventResponse>(`/api/events/${event.id}/zoho/invoice`, {
        method: 'POST',
      });
      onEvent(body.event);
      setNotice('Estimate converted to an invoice in Zoho Books.');
    } catch (caught: unknown) {
      setError(
        caught instanceof ApiClientError ? caught.message : 'Could not convert the estimate.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" data-testid="zoho-estimate-card">
      <h2>Zoho Books</h2>
      <p className="meta">
        Send a draft estimate with one quoted-price line. Line items are edited in Zoho after that.
        InventoHub does not email the customer.
      </p>
      {error ? <p className="status status--error">{error}</p> : null}
      {notice ? <p className="status status--ok">{notice}</p> : null}
      {!event.organizationId ? (
        <p className="meta">Assign an organization before sending an estimate.</p>
      ) : null}
      <label className="field">
        Quoted price (USD)
        <input
          inputMode="decimal"
          value={displayedPrice}
          onChange={(change) => setQuotedPrice(change.target.value)}
          disabled={hasEstimate || busy}
          data-testid="quoted-price"
        />
      </label>
      <button
        type="button"
        className="button"
        disabled={!canSend}
        onClick={() => void sendEstimate()}
        data-testid="send-estimate"
      >
        Send estimate
      </button>
      {event.zohoEstimateNumber ? (
        <p className="meta" data-testid="zoho-estimate-number">
          Estimate {event.zohoEstimateNumber}
        </p>
      ) : null}
      {event.zohoInvoiceNumber ? (
        <p className="meta" data-testid="zoho-invoice-number">
          Invoice {event.zohoInvoiceNumber}
        </p>
      ) : null}
      {event.zohoInvoiceStatus ? (
        <p className="meta" data-testid="zoho-invoice-status">
          Invoice status: {event.zohoInvoiceStatus}
        </p>
      ) : null}
      {hasEstimate && !hasInvoice ? (
        <button
          type="button"
          className="button button--ghost"
          disabled={!canConvert}
          onClick={() => void convertInvoice()}
          data-testid="convert-invoice"
        >
          Convert to invoice
        </button>
      ) : null}
    </div>
  );
}
